import { jwtDecode } from "jwt-decode"
import { getDoctorsList, getUser, getSharedUser } from "../services/http-service/api"
import { getShareToken } from "./shareToken"

export const ROLE_ADMIN = 0
export const ROLE_USER = 1
export const ROLE_DOCTOR = 2
export const ROLE_MANAGER = 3
export const ROLE_ENG = 4
export const ROLES = [ROLE_ADMIN, ROLE_USER, ROLE_DOCTOR, ROLE_MANAGER, ROLE_ENG]
export const ROLE_VALUES = ['ADMIN', 'USER', 'DOCTOR', 'MANAGER', 'ENG']

export const hasRole = (user, role) => (
  user?.roles?.includes(role) || user?.roles?.includes(ROLE_VALUES[role])
)

export const canViewDraftFilterPresets = (user) => (
  hasRole(user, ROLE_ADMIN) || hasRole(user, ROLE_ENG)
)

export const canImportExportExams = (user) => {
  if (isShareTokenAccess(user)) {
    return false
  }
  return hasRole(user, ROLE_ADMIN) || hasRole(user, ROLE_ENG)
}

export const isDraftFilterPreset = (preset) => preset?.isDraft === true

export const getVisibleFilterPresets = (presets, user) => (
  canViewDraftFilterPresets(user)
    ? presets
    : (presets || []).filter(preset => !isDraftFilterPreset(preset))
)

export const getRoleByValue = (value) => {
  const index = ROLE_VALUES.indexOf(value)
  return index !== -1 ? ROLES[index] : null
}

export const getGender = (patient) => {
  return patient &&
    patient.profile &&
    (/^male$/i).test(patient.profile.data.gender)
    ? "Мужской"
    : patient &&
      patient.profile &&
      (/^female$/i).test(patient.profile.data.gender) ? "Женский" : ""
}

export const getOnlyAge = (birthDate) =>
  Math.floor((new Date() - new Date(birthDate).getTime()) / 3.15576e10)

const fetchDoctorsList = async () => {
  const doctorsResponse = await getDoctorsList()
  if (doctorsResponse.status === 200) {
    return doctorsResponse.data
  }
  if (doctorsResponse.status === 404) {
    return null // invalid
  }
  return undefined // other errors, continue
}

const fetchRoles = async (user) => {
  if (user.shared) return user

  let doctorsList = null

  // Process ROLE_DOCTOR
  if (user.roles.includes(ROLE_DOCTOR)) {
    doctorsList = await fetchDoctorsList()
    if (doctorsList === null) return null // invalid token
    if (doctorsList) {
      const doctor = doctorsList.find(d => d.userId === user.uid)
      user.doctor = doctor
    }
  }

  // Process ROLE_USER
  if (user.roles.includes(ROLE_USER)) {
    const userResponse = await getUser(user.uid)
    if (userResponse && userResponse.data) {
      user.user = userResponse.data

      const doctorId = +userResponse.data.profile?.data?.['doctor']
      if (doctorId) {
        if (!doctorsList) {
          doctorsList = await fetchDoctorsList()
          if (doctorsList === null) return null // invalid token
        }
        if (doctorsList) {
          const doctor = doctorsList.find(d => d.id === doctorId)
          if (!user.doctor) {
            user.doctor = doctor
          }
        }
      }
    }
  }

  return user
}

// A bearer token that is malformed, expired, or no longer accepted by the
// backend must be dropped from storage before any exam/visit/etc. request is
// made with it - otherwise the app is stuck showing a broken session instead
// of falling back to the share token already in the URL.
export const getUserFromToken = async (storedToken) => {
  const shareToken = getShareToken()

  if (!storedToken) {
    return shareToken ? await getSharedVisitor(shareToken) : null
  }

  const tokenData = storedToken.split(" ", 2)
  if (tokenData.length !== 2) {
    localStorage.removeItem('token')
    return shareToken ? await getSharedVisitor(shareToken) : null
  }

  try {
    const token = jwtDecode(tokenData[1])
    if (isTokenExpired(token)) {
      localStorage.removeItem('token')
      return shareToken ? await getSharedVisitor(shareToken) : null
    }

    const roles = token.roles || [token.role]
    const visitor = {
      ...token,
      roles,
      token: tokenData[1],
      id: token.uid,
      uid: token.uid,
      isAdmin: roles.includes(ROLE_ADMIN)
    }

    const resolvedVisitor = await fetchRoles(visitor)
    if (resolvedVisitor === null) {
      // The backend rejected this token (e.g. the user behind it no longer exists).
      localStorage.removeItem('token')
      return shareToken ? await getSharedVisitor(shareToken) : null
    }
    return resolvedVisitor
  } catch (error) {
    console.error("Error decoding token or fetching user data:", error)
    localStorage.removeItem('token')
    return shareToken ? await getSharedVisitor(shareToken) : null
  }
}

const getSharedVisitor = async (shareToken) => {
  const sharedResponse = await getSharedUser(shareToken)
  if (sharedResponse.status !== 200 || !sharedResponse.data) return null

  const user = sharedResponse.data
  const roles = user.roles.map(getRoleByValue)
  const visitor = {
    ...user,
    id: user.userId,
    uid: user.userId,
    token: shareToken,
    roles,
    isAdmin: roles.includes(ROLE_ADMIN) || false,
    shared: true,
  }
  if (roles.includes(ROLE_DOCTOR) && user.doctorId) {
    visitor.doctor = {
      id: user.doctorId,
      profile: { data: { fullName: user.fullName } },
    }
  }
  return await fetchRoles(visitor)
}

const isTokenExpired = (token) => (
  typeof token.exp === 'number' && Date.now() >= token.exp * 1000
)

export const isShareTokenAccess = (visitor) => {
  return !!(visitor && visitor.shared)
}

export const patientsAreAnonymous = (visitor) => {
  return visitor && visitor.doctor && visitor.doctor.groups && visitor.doctor.groups.length > 0 && visitor.doctor.groups[0].profile.data['patients_are_anonymous']
}

/**
 * @typedef {object} PatientIdentitySettings
 * @property {boolean} anonymous
 * @property {string | undefined} identityType
 */

/**
 * @param {{groups?: Array<{profile?: import('../types/profile').Profile<import('../types/profile').DoctorGroupProfileData>}>}} doctor
 * @returns {PatientIdentitySettings} Настройки отображения идентификатора пациента.
 */
export const getPatientIdentitySettings = (doctor) => {
  const doctorProfileData = /** @type {import('../types/profile').DoctorGroupProfileData} */ (
    doctor?.groups?.[0]?.profile?.data || {}
  )
  return {
    anonymous: doctorProfileData.patients_are_anonymous === true,
    identityType: doctorProfileData.patients_identity_type,
  }
}

export const patientsAreShared = (visitor) => {
  return visitor && visitor.doctor && visitor.doctor.groups && visitor.doctor.groups.length > 0 && visitor.doctor.groups[0].profile.data['patients_are_shared']
}

export const DEMO_DOCTOR_ID = 0

/**
 * @param {{doctorIds?: Array<number|string>}|undefined} group
 * @param {number|string|null|undefined} doctorId
 * @returns {boolean} Whether the doctor is a member of the group.
 */
export const groupHasDoctor = (group, doctorId) =>
  doctorId != null && group?.doctorIds?.some(id => String(id) === String(doctorId)) === true

/**
 * Returns visible exam owner IDs.
 * @param {{id?: number, groups?: Array<{id: number, doctorIds?: number[], profile?: {data?: Record<string, unknown>}}>}} doctor
 * @param {number | string | null | undefined} demoPatientGroupId Demo patient group ID.
 * @returns {Set<string>}
 */
const getVisibleDoctorIds = (doctor, demoPatientGroupId) => {
  const visible = new Set()
  if (!doctor?.id) return visible
  visible.add(String(doctor.id))

  if (patientsAreShared({ doctor })) {
    (doctor.groups?.[0]?.doctorIds || []).forEach(id => visible.add(String(id)))
  }

  const inDemoGroup = demoPatientGroupId != null &&
    doctor.groups?.some(group => String(group.id) === String(demoPatientGroupId))
  if (inDemoGroup) {
    visible.add(String(DEMO_DOCTOR_ID))
  }

  return visible
}

const canDoctorAssignExam = (visit, doctor, demoPatientGroupId) =>
  visit.doctorId != null &&
  getVisibleDoctorIds(doctor, demoPatientGroupId).has(String(visit.doctorId))

/**
 * Whether the current visitor may assign an exam from the consultation UI.
 * Mirrors the backend check, which uses the patient's current doctor.
 * @param {{doctorId?: number, userId?: number}} visit doctorId is the patient's current doctor
 * @param {{uid?: number, roles?: Array<number|string>, doctor?: object}} visitor
 * @param {number|string|null|undefined} demoPatientGroupId
 * @returns {boolean} Whether the current visitor may assign an exam to the patient.
 */
export const canAssignExamToVisit = (visit, visitor, demoPatientGroupId) => {
  if (!visit || !visitor || isShareTokenAccess(visitor)) return false
  if (hasRole(visitor, ROLE_ADMIN)) return true
  if (hasRole(visitor, ROLE_DOCTOR)) return canDoctorAssignExam(visit, visitor.doctor, demoPatientGroupId)
  if (hasRole(visitor, ROLE_USER)) return String(visit.userId) === String(visitor.uid)

  return false
}

/**
 * Checks whether the exam is visible to the visitor.
 * @param {{doctorId?: number}} exam Exam data.
 * @param {object} visitor Current visitor.
 * @param {number | string | null | undefined} demoPatientGroupId Demo patient group ID.
 * @returns {boolean | null} Visibility, or null when unknown.
 */
export const isExamVisibleToDoctor = (exam, visitor, demoPatientGroupId) => {
  if (!exam || exam.doctorId == null || !visitor) return null
  if (!hasRole(visitor, ROLE_DOCTOR) || !visitor.doctor?.id) return null
  if (hasRole(visitor, ROLE_ADMIN) || hasRole(visitor, ROLE_MANAGER)) return true

  return getVisibleDoctorIds(visitor.doctor, demoPatientGroupId).has(String(exam.doctorId))
}

export const aiAllowed = (visitor) => {
  return visitor && visitor.doctor && visitor.doctor.groups && visitor.doctor.groups.length > 0 && visitor.doctor.groups[0].profile.data['ai_allowed']
}

export const visitsEnabled = (visitor) => {
  return visitor && visitor.doctor && visitor.doctor.groups && visitor.doctor.groups.length > 0 && visitor.doctor.groups[0].profile.data['visits_enabled']
}

export const AI_DECISION_NOT_ENOUGH_DATA = 'Недостаточно данных для достоверной оценки'
export const AI_DECISION_PATHOLOGY_DETECTED = 'Обнаружены признаки, требующие внимания'
export const AI_DECISION_NO_PATHOLOGY = 'Признаков патологии не обнаружено'

const latestAiComment = (comments, aiDoctorIds) => {
  return (comments || [])
    .filter(c => aiDoctorIds.includes(c.doctorId))
    .reduce((latest, c) => (
      !latest || new Date(c.createdAt) > new Date(latest.createdAt) ? c : latest
    ), null)
}

/**
 * Short AI decision status for an exam, mirroring the mobile apps' aiDecisionStatus().
 * Returns null when there's nothing to show (no AI comment yet, or not in b2c mode).
 * @param {object} exam - Exam with a `comments` array.
 * @param {number[]} aiDoctorIds - Doctor IDs identifying AI-authored comments.
 * @param {boolean} isB2C - Whether the current context is b2c (visits enabled).
 */
export const aiDecisionStatus = (exam, aiDoctorIds, isB2C) => {
  const comment = latestAiComment(exam?.comments, aiDoctorIds)
  if (!comment) return null
  if (comment.noise === true) return AI_DECISION_NOT_ENOUGH_DATA
  if (!isB2C) return null
  return comment.pathology === true ? AI_DECISION_PATHOLOGY_DETECTED : AI_DECISION_NO_PATHOLOGY
}

export const examStatus = (exam) => {
  let status = exam.profile?.data?.external_id ? "DAMUMED" : exam.status
  switch (status) {
    case 'OPEN':
      return exam.byDoctor ? "Назначено" : "Направлено доктору"
    case 'DAMUMED':
      return 'Направлено в Damumed'
    case 'CLOSED':
      return 'Получен'
    case 'ACTIVE':
      return 'Выполняется'
    case 'INACTIVE':
      return 'Пройдено'
    case 'UPLOADING':
      return 'В процессе выгрузки'
  }
}

export const aiStatus = (point) => {
  const pointData = JSON.parse(point && point.media?.profile?.data?.external_data2 || "{}")
  console.log('Point', pointData)
  const result = pointData.results?.find(result => result.filename == point.media.fileName)
  console.log('Result', result)
  return `AI-заключение: ${result ? 'получено' : 'не получено'}`
  /*
  const name = pointData.data && pointData.data.length > 0 && pointData.data[0].result.aiResult.probParams.class
      && pointData.data[0].result.aiResult.probParams.class.length > 0 ?
      pointData.data[0].result.aiResult.probParams.class[0].name : ""
  if (name === 'bad_endo') {
      return "AI заключение: не является эндоскопией уха"
  }
  if (name === 'nose') {
    return 'AI заключение: Риноскопия'
  }
  if (name === 'normal') {
    return 'AI заключение: 🟢 Нет патологии'
  }
  if (name === 'pathology') {
    return 'AI заключение: 🔴 Патология'
  }
  return `AI-заключение: ${name || 'не получено'}`
  */
}

const filterAIResults = (examTypeId, total, tags) => {
  if (total === 1) {
    return true
  }
  switch (examTypeId) {
    case 3:
      return tags.includes('Ухо')
    case 4:
      return tags.includes('Нос')
    default:
      return false
  }
}

export const parseAIResponse = (examTypeId, media) => {
  const fps = media.profile.data['frames'] / media.profile.data['length']
  const data = media.profile.data['external_data2'] && JSON.parse(media.profile.data['external_data2'])
  const info = data && data.results && data.results.length > 0 && data.results[0].results_info
  const status = info && info.results ? info.results
    .filter(r => filterAIResults(examTypeId, info.results.length, r.cascade ? Object.values(r.cascade) : []))
    .map(result => {
      const model = {
        model: `${result.model}`,
        tags: result.cascade ? Object.values(result.cascade).join(',') : '',
        status: []
      }
      result.prediction_classes.map(prediction => {
        const start_time = Math.round(prediction.video_segments.start_frame / fps * 100) / 100
        const end_time = Math.round(prediction.video_segments.end_frame / fps * 100) / 100
        model.status.push({
          name: prediction.name,
          confidence: prediction.confidence,
          start: start_time,
          end: end_time
        })
      })
      return model
    }) : []
  return status
}
